/**
 * Browser projection for the legacy public-profile read.
 *
 * This is defense in depth while the Vercel public creator read model remains blocked;
 * it is not a substitute for server-side visibility, RLS, or capability enforcement.
 */
export const PUBLIC_PROFILE_SELECT = [
  'id',
  'user_id',
  'username',
  'display_name',
  'bio',
  'avatar_url',
  'social_links',
  'is_public',
].join(', ');

export const PUBLIC_DISPLAY_YEAR_SELECT = 'id, year';

export const PUBLIC_DISPLAY_SONG_SELECT = [
  'id',
  'title',
  'artist',
  'duration',
  'year_introduced',
  'genre',
  'album_cover_url',
  'video_url',
].join(', ');

export const PUBLIC_PROFILE_LINK_KEYS = [
  'tiktok',
  'facebook',
  'website',
  'youtube',
  'instagram',
] as const;

export type PublicProfileLinkKey = (typeof PUBLIC_PROFILE_LINK_KEYS)[number];

export type PublicProfileSocialLinks = Partial<Record<PublicProfileLinkKey, string>>;

/**
 * Transport-neutral shape for the future Vercel-owned public creator read.
 *
 * This contract deliberately contains only public identity, coarse location,
 * one public display summary, a server-owned metric projection, and the
 * canonical Algolia inventory handoff. Metrics stay null when the server
 * cannot prove a complete public result; the legacy browser projection must
 * not derive them.
 */
export const PUBLIC_CREATOR_PROFILE_READ_MODEL_VERSION = 'creator-profile-public-v1' as const;
export const PUBLIC_CREATOR_PROFILE_INDEX_NAME = 'sequences' as const;

export interface PublicCreatorProfileMetrics {
  sequenceCount: number | null;
  downloadCount: number | null;
  averageRating: number | null;
  reviewCount: number | null;
}

export interface PublicCreatorProfileSource {
  userId: string;
  username: string;
  displayName?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  socialLinks?: unknown;
  isPublic: boolean;
  city?: string | null;
  state?: string | null;
  display?: {
    id: string;
    name: string;
    imageUrl?: string | null;
    city?: string | null;
    state?: string | null;
  } | null;
  metrics?: Partial<PublicCreatorProfileMetrics> | null;
}

export interface PublicCreatorProfileReadModel {
  schemaVersion: typeof PUBLIC_CREATOR_PROFILE_READ_MODEL_VERSION;
  visibility: 'public';
  profile: {
    userId: string;
    username: string;
    displayName: string;
    bio: string | null;
    avatarUrl: string | null;
    socialLinks: PublicProfileSocialLinks | null;
    location: { city: string; state: string } | null;
  };
  display: {
    id: string;
    name: string;
    imageUrl: string | null;
    location: { city: string; state: string } | null;
  } | null;
  inventory: {
    indexName: typeof PUBLIC_CREATOR_PROFILE_INDEX_NAME;
    filter: string;
  };
  metrics: PublicCreatorProfileMetrics;
}

/**
 * Lazy public-profile sections are a separate versioned boundary so the
 * browser can migrate one panel at a time without weakening the v1 shell.
 * Sequence inventory remains an Algolia handoff; songs and check-ins are
 * bounded, visibility-filtered server projections.
 */
export const PUBLIC_CREATOR_PROFILE_SECTION_READ_MODEL_VERSION = 'creator-profile-section-v1' as const;
export const PUBLIC_CREATOR_PROFILE_SECTION_NAMES = [
  'about',
  'display',
  'sequences',
  'videos',
  'checkins',
  'reviews',
] as const;

export type PublicCreatorProfileSection = (typeof PUBLIC_CREATOR_PROFILE_SECTION_NAMES)[number];

export interface PublicCreatorProfileSectionPageInfo {
  limit: number;
  offset: number;
  nextCursor: string | null;
}

export interface PublicCreatorProfileDisplaySection {
  id: string;
  slug: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  photos: string[];
  displayType: string | null;
  holidays: string[];
  rating: number | null;
  reviewRating: number | null;
  location: { city: string; state: string } | null;
  songCount: number | null;
}

export interface PublicCreatorProfileSong {
  id: string;
  title: string;
  artist: string | null;
  duration: string | null;
  year: number | null;
  genre: string | null;
  albumCoverUrl: string | null;
  videoUrl: string | null;
}

export interface PublicCreatorProfileCheckIn {
  id: string;
  displayId: string;
  display: {
    id: string;
    name: string;
    location: { city: string; state: string } | null;
  } | null;
  checkedInAt: string;
  rating: number | null;
  note: string | null;
  locationVerified: boolean;
}

export interface PublicCreatorProfileAchievement {
  id: string;
  type: string;
  earnedAt: string | null;
}

export interface PublicCreatorProfileReview {
  id: string;
  targetType: 'sequence';
  targetId: string;
  targetName: string | null;
  rating: number | null;
  title: string | null;
  content: string | null;
  recommended: boolean | null;
  createdAt: string | null;
}

export interface PublicCreatorProfileSectionSource {
  section: PublicCreatorProfileSection;
  profile: PublicCreatorProfileSource;
  display?: unknown;
  songs?: unknown[];
  songTotal?: number | null;
  checkins?: unknown[];
  checkinTotal?: number | null;
  uniqueDisplays?: number | null;
  achievements?: unknown[];
  reviews?: unknown[];
  reviewTotal?: number | null;
  pageInfo: PublicCreatorProfileSectionPageInfo;
}

export interface PublicCreatorProfileSectionReadModel {
  schemaVersion: typeof PUBLIC_CREATOR_PROFILE_SECTION_READ_MODEL_VERSION;
  visibility: 'public';
  section: PublicCreatorProfileSection;
  profile: PublicCreatorProfileReadModel['profile'];
  display: PublicCreatorProfileDisplaySection | null;
  inventory: PublicCreatorProfileReadModel['inventory'];
  songs: {
    items: PublicCreatorProfileSong[];
    total: number | null;
    nextCursor: string | null;
    pageInfo: PublicCreatorProfileSectionPageInfo;
  } | null;
  checkins: {
    items: PublicCreatorProfileCheckIn[];
    total: number | null;
    uniqueDisplays: number | null;
    achievements: PublicCreatorProfileAchievement[];
    nextCursor: string | null;
    pageInfo: PublicCreatorProfileSectionPageInfo;
  } | null;
  reviews?: {
    items: PublicCreatorProfileReview[];
    total: number | null;
    nextCursor: string | null;
    pageInfo: PublicCreatorProfileSectionPageInfo;
  } | null;
}

/**
 * Normalizes the human-entered username forms accepted by the legacy lookup.
 * This is intentionally limited to matching; it does not rewrite the URL or
 * change the username displayed to visitors.
 */
export const normalizePublicProfileUsername = (value: unknown): string => (
  typeof value === 'string'
    ? value.trim().toLocaleLowerCase().replace(/\s+/g, '')
    : ''
);

const trimOptional = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const normalizeCoarseLocation = (
  city: unknown,
  state: unknown,
): { city: string; state: string } | null => {
  const normalizedCity = trimOptional(city);
  const normalizedState = trimOptional(state);
  return normalizedCity && normalizedState
    ? { city: normalizedCity, state: normalizedState }
    : null;
};

const normalizeHttpUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed && isHttpUrl(trimmed) ? trimmed : null;
};

const normalizeNumber = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const normalizeNonNegativeInteger = (value: unknown): number | null => {
  const normalized = normalizeNumber(value);
  return normalized !== null && Number.isSafeInteger(normalized) && normalized >= 0
    ? normalized
    : null;
};

const normalizeRating = (value: unknown): number | null => {
  const normalized = normalizeNumber(value);
  if (normalized === null || normalized < 0 || normalized > 5) return null;
  return Math.round(normalized * 10) / 10;
};

export const normalizePublicCreatorProfileMetrics = (
  value: unknown,
): PublicCreatorProfileMetrics => {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

  return {
    sequenceCount: normalizeNonNegativeInteger(source.sequenceCount),
    downloadCount: normalizeNonNegativeInteger(source.downloadCount),
    averageRating: normalizeRating(source.averageRating),
    reviewCount: normalizeNonNegativeInteger(source.reviewCount),
  };
};

const normalizePublicDisplaySection = (value: unknown): PublicCreatorProfileDisplaySection | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const id = trimOptional(source.id);
  const name = trimOptional(source.name);
  if (!id || !name) return null;

  const photos = Array.isArray(source.photos)
    ? source.photos.map(normalizeHttpUrl).filter((photo): photo is string => Boolean(photo))
    : [];
  const holidays = Array.isArray(source.holidays)
    ? source.holidays
      .map(trimOptional)
      .filter((holiday): holiday is string => Boolean(holiday))
    : [];

  return {
    id,
    slug: trimOptional(source.slug),
    name,
    description: trimOptional(source.description),
    imageUrl: normalizeHttpUrl(source.image_url ?? source.imageUrl),
    photos,
    displayType: trimOptional(source.display_type ?? source.displayType),
    holidays,
    rating: normalizeNumber(source.rating),
    reviewRating: normalizeNumber(source.review_rating ?? source.reviewRating),
    location: normalizeCoarseLocation(source.city, source.state),
    songCount: normalizeNumber(source.songCount ?? source.song_count),
  };
};

const normalizePublicSongs = (values: unknown[] | undefined): PublicCreatorProfileSong[] => (
  (values || []).flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const source = value as Record<string, unknown>;
    const id = trimOptional(source.id);
    const title = trimOptional(source.title);
    if (!id || !title) return [];
    return [{
      id,
      title,
      artist: trimOptional(source.artist),
      duration: trimOptional(source.duration),
      year: normalizeNumber(source.year_introduced ?? source.year),
      genre: trimOptional(source.genre),
      albumCoverUrl: normalizeHttpUrl(source.album_cover_url ?? source.albumCoverUrl),
      videoUrl: normalizeHttpUrl(source.video_url ?? source.videoUrl),
    }];
  })
);

const normalizePublicCheckins = (values: unknown[] | undefined): PublicCreatorProfileCheckIn[] => (
  (values || []).flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const source = value as Record<string, unknown>;
    const id = trimOptional(source.id);
    const displayId = trimOptional(source.display_id ?? source.displayId);
    const checkedInAt = trimOptional(source.checked_in_at ?? source.checkedInAt);
    if (!id || !displayId || !checkedInAt) return [];

    const displaySource = source.displays ?? source.display;
    const display = displaySource && typeof displaySource === 'object'
      ? normalizePublicDisplaySection(displaySource)
      : null;
    return [{
      id,
      displayId,
      display: display
        ? { id: display.id, name: display.name, location: display.location }
        : null,
      checkedInAt,
      rating: normalizeNumber(source.rating),
      note: trimOptional(source.note),
      locationVerified: source.location_verified === true || source.locationVerified === true,
    }];
  })
);

const normalizePublicAchievements = (values: unknown[] | undefined): PublicCreatorProfileAchievement[] => (
  (values || []).flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const source = value as Record<string, unknown>;
    const id = trimOptional(source.id);
    const type = trimOptional(source.achievement_type ?? source.type);
    if (!id || !type) return [];
    return [{ id, type, earnedAt: trimOptional(source.earned_at ?? source.earnedAt) }];
  })
);

const normalizeReviewRating = (value: unknown): number | null => {
  const rating = normalizeNumber(value);
  return rating !== null && rating >= 1 && rating <= 5
    ? Math.round(rating * 10) / 10
    : null;
};

const normalizePublicReviews = (values: unknown[] | undefined): PublicCreatorProfileReview[] => (
  (values || []).flatMap((value) => {
    if (!value || typeof value !== 'object') return [];
    const source = value as Record<string, unknown>;
    const id = trimOptional(source.id);
    const targetId = trimOptional(source.target_id ?? source.targetId);
    const targetType = trimOptional(source.target_type ?? source.targetType);
    if (!id || !targetId || targetType !== 'sequence') return [];
    return [{
      id,
      targetType: 'sequence' as const,
      targetId,
      targetName: trimOptional(source.target_name ?? source.targetName),
      rating: normalizeReviewRating(source.rating),
      title: trimOptional(source.title),
      content: trimOptional(source.content),
      recommended: typeof source.recommended === 'boolean' ? source.recommended : null,
      createdAt: trimOptional(source.created_at ?? source.createdAt),
    }];
  })
);

/**
 * Builds the reviewed public profile boundary for the canonical public read.
 * Exact address, coordinates, private metadata, and browser-derived metrics
 * have no representation in this model.
 */
export const toPublicCreatorProfileReadModel = (
  source: PublicCreatorProfileSource,
): PublicCreatorProfileReadModel | null => {
  if (!source.isPublic) return null;

  const userId = trimOptional(source.userId);
  const username = trimOptional(source.username);
  if (!userId || !username) return null;

  const display = source.display?.id && source.display.name
    ? {
        id: source.display.id,
        name: source.display.name.trim(),
        imageUrl: trimOptional(source.display.imageUrl),
        location: normalizeCoarseLocation(source.display.city, source.display.state),
      }
    : null;

  return {
    schemaVersion: PUBLIC_CREATOR_PROFILE_READ_MODEL_VERSION,
    visibility: 'public',
    profile: {
      userId,
      username,
      displayName: trimOptional(source.displayName) || username,
      bio: trimOptional(source.bio),
      avatarUrl: trimOptional(source.avatarUrl),
      socialLinks: normalizePublicProfileSocialLinks(source.socialLinks),
      location: normalizeCoarseLocation(source.city, source.state),
    },
    display,
    inventory: {
      indexName: PUBLIC_CREATOR_PROFILE_INDEX_NAME,
      filter: `creator_id:${userId}`,
    },
    metrics: normalizePublicCreatorProfileMetrics(source.metrics),
  };
};

/**
 * Normalizes one lazy public-profile panel. The projection intentionally
 * strips exact addresses, coordinates, schedules, and private user fields.
 */
export const toPublicCreatorProfileSectionReadModel = (
  source: PublicCreatorProfileSectionSource,
): PublicCreatorProfileSectionReadModel | null => {
  const base = toPublicCreatorProfileReadModel(source.profile);
  if (!base) return null;

  const model: PublicCreatorProfileSectionReadModel = {
    schemaVersion: PUBLIC_CREATOR_PROFILE_SECTION_READ_MODEL_VERSION,
    visibility: 'public',
    section: source.section,
    profile: base.profile,
    display: normalizePublicDisplaySection(source.display),
    inventory: base.inventory,
    songs: null,
    checkins: null,
    reviews: null,
  };

  if (source.section === 'videos') {
    const items = normalizePublicSongs(source.songs);
    model.songs = {
      items,
      total: typeof source.songTotal === 'number' ? source.songTotal : null,
      nextCursor: source.pageInfo.nextCursor,
      pageInfo: source.pageInfo,
    };
  }

  if (source.section === 'checkins') {
    model.checkins = {
      items: normalizePublicCheckins(source.checkins),
      total: typeof source.checkinTotal === 'number' ? source.checkinTotal : null,
      uniqueDisplays: typeof source.uniqueDisplays === 'number' ? source.uniqueDisplays : null,
      achievements: normalizePublicAchievements(source.achievements),
      nextCursor: source.pageInfo.nextCursor,
      pageInfo: source.pageInfo,
    };
  }

  if (source.section === 'reviews') {
    model.reviews = {
      items: normalizePublicReviews(source.reviews),
      total: typeof source.reviewTotal === 'number' ? source.reviewTotal : null,
      nextCursor: source.pageInfo.nextCursor,
      pageInfo: source.pageInfo,
    };
  }

  return model;
};

const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Keeps public social links safe for use as external anchors. Invalid values
 * are dropped rather than rendered as hrefs; the server/RLS contract remains
 * the authority for visibility and ownership.
 */
export const normalizePublicProfileSocialLinks = (
  value: unknown,
): PublicProfileSocialLinks | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const links: PublicProfileSocialLinks = {};
  for (const key of PUBLIC_PROFILE_LINK_KEYS) {
    const candidate = (value as Record<string, unknown>)[key];
    if (typeof candidate !== 'string') {
      continue;
    }

    const trimmed = candidate.trim();
    if (trimmed && isHttpUrl(trimmed)) {
      links[key] = trimmed;
    }
  }

  return Object.keys(links).length > 0 ? links : null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isNullableString = (value: unknown): value is string | null => (
  value === null || typeof value === 'string'
);

const isNullableNumber = (value: unknown): value is number | null => (
  value === null || (typeof value === 'number' && Number.isFinite(value))
);

const isNullableNonNegativeInteger = (value: unknown): value is number | null => (
  value === null
    || (typeof value === 'number' && Number.isSafeInteger(value) && value >= 0)
);

const isNullableRating = (value: unknown): value is number | null => (
  value === null
    || (typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 5)
);

const isPublicCreatorProfileMetrics = (value: unknown): value is PublicCreatorProfileMetrics => (
  isRecord(value)
    && isNullableNonNegativeInteger(value.sequenceCount)
    && isNullableNonNegativeInteger(value.downloadCount)
    && isNullableRating(value.averageRating)
    && isNullableNonNegativeInteger(value.reviewCount)
);

const isCoarseLocation = (value: unknown): value is { city: string; state: string } | null => {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  return typeof value.city === 'string'
    && value.city.trim().length > 0
    && typeof value.state === 'string'
    && value.state.trim().length > 0;
};

const isSafeSocialLinks = (value: unknown): value is PublicProfileSocialLinks | null => {
  if (value === null) return true;
  if (!isRecord(value)) return false;

  return Object.entries(value).every(([key, candidate]) => (
    (PUBLIC_PROFILE_LINK_KEYS as readonly string[]).includes(key)
      && typeof candidate === 'string'
      && isHttpUrl(candidate)
  ));
};

const isPublicProfileEnvelope = (
  value: unknown,
): value is PublicCreatorProfileReadModel['profile'] => {
  if (!isRecord(value)) return false;
  return typeof value.userId === 'string'
    && value.userId.trim().length > 0
    && typeof value.username === 'string'
    && value.username.trim().length > 0
    && typeof value.displayName === 'string'
    && value.displayName.trim().length > 0
    && isNullableString(value.bio)
    && isNullableString(value.avatarUrl)
    && isSafeSocialLinks(value.socialLinks)
    && isCoarseLocation(value.location);
};

const isPublicDisplaySummary = (value: unknown): boolean => {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  return typeof value.id === 'string'
    && value.id.trim().length > 0
    && typeof value.name === 'string'
    && value.name.trim().length > 0
    && isNullableString(value.imageUrl)
    && isCoarseLocation(value.location);
};

const isPublicDisplaySection = (value: unknown): boolean => {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  const photos = value.photos;
  const holidays = value.holidays;
  return typeof value.id === 'string'
    && value.id.trim().length > 0
    && isNullableString(value.slug)
    && typeof value.name === 'string'
    && value.name.trim().length > 0
    && isNullableString(value.description)
    && isNullableString(value.imageUrl)
    && Array.isArray(photos)
    && photos.every((photo) => typeof photo === 'string' && isHttpUrl(photo))
    && Array.isArray(holidays)
    && holidays.every((holiday) => typeof holiday === 'string' && holiday.trim().length > 0)
    && isNullableString(value.displayType)
    && isNullableNumber(value.rating)
    && isNullableNumber(value.reviewRating)
    && isCoarseLocation(value.location)
    && isNullableNumber(value.songCount);
};

const isProfileInventory = (value: unknown): value is PublicCreatorProfileReadModel['inventory'] => (
  isRecord(value)
    && value.indexName === PUBLIC_CREATOR_PROFILE_INDEX_NAME
    && typeof value.filter === 'string'
    && value.filter.trim().length > 0
);

const isProfilePageInfo = (value: unknown): value is PublicCreatorProfileSectionPageInfo => (
  isRecord(value)
    && typeof value.limit === 'number'
    && Number.isInteger(value.limit)
    && value.limit > 0
    && typeof value.offset === 'number'
    && Number.isInteger(value.offset)
    && value.offset >= 0
    && (value.nextCursor === null || typeof value.nextCursor === 'string')
);

const isPublicSong = (value: unknown): boolean => (
  isRecord(value)
    && typeof value.id === 'string'
    && value.id.trim().length > 0
    && typeof value.title === 'string'
    && value.title.trim().length > 0
    && isNullableString(value.artist)
    && isNullableString(value.duration)
    && isNullableNumber(value.year)
    && isNullableString(value.genre)
    && (value.albumCoverUrl === null || (typeof value.albumCoverUrl === 'string' && isHttpUrl(value.albumCoverUrl)))
    && (value.videoUrl === null || (typeof value.videoUrl === 'string' && isHttpUrl(value.videoUrl)))
);

const isPublicCheckIn = (value: unknown): boolean => {
  if (!isRecord(value)) return false;
  const display = value.display;
  const validDisplay = display === null || (
    isRecord(display)
      && typeof display.id === 'string'
      && display.id.trim().length > 0
      && typeof display.name === 'string'
      && display.name.trim().length > 0
      && isCoarseLocation(display.location)
  );
  return typeof value.id === 'string'
    && value.id.trim().length > 0
    && typeof value.displayId === 'string'
    && value.displayId.trim().length > 0
    && validDisplay
    && typeof value.checkedInAt === 'string'
    && value.checkedInAt.trim().length > 0
    && isNullableNumber(value.rating)
    && isNullableString(value.note)
    && typeof value.locationVerified === 'boolean';
};

const isPublicAchievement = (value: unknown): boolean => (
  isRecord(value)
    && typeof value.id === 'string'
    && value.id.trim().length > 0
    && typeof value.type === 'string'
    && value.type.trim().length > 0
    && isNullableString(value.earnedAt)
);

const isPublicReview = (value: unknown): value is PublicCreatorProfileReview => (
  isRecord(value)
    && typeof value.id === 'string'
    && value.id.trim().length > 0
    && value.targetType === 'sequence'
    && typeof value.targetId === 'string'
    && value.targetId.trim().length > 0
    && isNullableString(value.targetName)
    && isNullableRating(value.rating)
    && isNullableString(value.title)
    && isNullableString(value.content)
    && (value.recommended === null || typeof value.recommended === 'boolean')
    && isNullableString(value.createdAt)
);

export const isPublicCreatorProfileReadModel = (
  value: unknown,
): value is PublicCreatorProfileReadModel => {
  if (!isRecord(value)) return false;
  const metrics = value.metrics;
  return value.schemaVersion === PUBLIC_CREATOR_PROFILE_READ_MODEL_VERSION
    && value.visibility === 'public'
    && isPublicProfileEnvelope(value.profile)
    && isPublicDisplaySummary(value.display)
    && isProfileInventory(value.inventory)
    && isPublicCreatorProfileMetrics(metrics);
};

export const isPublicCreatorProfileSectionReadModel = (
  value: unknown,
): value is PublicCreatorProfileSectionReadModel => {
  if (!isRecord(value)) return false;
  const songs = value.songs;
  const checkins = value.checkins;
  const reviews = value.reviews;
  const validSongs = songs === null || (
    isRecord(songs)
      && Array.isArray(songs.items)
      && songs.items.every(isPublicSong)
      && isNullableNumber(songs.total)
      && (songs.nextCursor === null || typeof songs.nextCursor === 'string')
      && isProfilePageInfo(songs.pageInfo)
  );
  const validCheckins = checkins === null || (
    isRecord(checkins)
      && Array.isArray(checkins.items)
      && checkins.items.every(isPublicCheckIn)
      && isNullableNumber(checkins.total)
      && isNullableNumber(checkins.uniqueDisplays)
      && Array.isArray(checkins.achievements)
      && checkins.achievements.every(isPublicAchievement)
      && (checkins.nextCursor === null || typeof checkins.nextCursor === 'string')
      && isProfilePageInfo(checkins.pageInfo)
  );
  const validReviews = reviews === undefined || reviews === null || (
    isRecord(reviews)
      && Array.isArray(reviews.items)
      && reviews.items.every(isPublicReview)
      && isNullableNumber(reviews.total)
      && (reviews.nextCursor === null || typeof reviews.nextCursor === 'string')
      && isProfilePageInfo(reviews.pageInfo)
  );

  return value.schemaVersion === PUBLIC_CREATOR_PROFILE_SECTION_READ_MODEL_VERSION
    && value.visibility === 'public'
    && (PUBLIC_CREATOR_PROFILE_SECTION_NAMES as readonly string[]).includes(String(value.section))
    && isPublicProfileEnvelope(value.profile)
    && isPublicDisplaySection(value.display)
    && isProfileInventory(value.inventory)
    && validSongs
    && validCheckins
    && validReviews
    && (value.section !== 'videos' || songs !== null)
    && (value.section !== 'checkins' || checkins !== null)
    && (value.section !== 'reviews' || (reviews !== undefined && reviews !== null));
};
